import { AlertTriangle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import type { ExportGapSection } from '@/lib/exportGaps'

interface ExportGapConfirmDialogProps {
  open: boolean
  sections: ExportGapSection[]
  attachmentCount: number
  onExportAnyway: () => void
  onReviewGaps: () => void
  onOpenChange: (open: boolean) => void
}

/**
 * Pre-export confirmation (spec 19).
 *
 * The export strips annotations whatever the author clicks — this dialog exists
 * so the strip is not silent. Two distinct messages live here:
 *
 *  - a GAP is removed on export, so the surrounding claim ships reading like
 *    finished prose with nothing behind it;
 *  - a BROKEN marker is not removed, because an unclosed token never matches the
 *    scanner. It reaches the evaluator verbatim. Only the editor can fix it.
 *
 * Folding the second into the first would tell the author the opposite of what
 * happens, so the copy and the count are kept apart.
 */
export function ExportGapConfirmDialog({
  open,
  sections,
  attachmentCount,
  onExportAnyway,
  onReviewGaps,
  onOpenChange,
}: ExportGapConfirmDialogProps) {
  const totalTodos = sections.reduce((total, section) => total + section.todos.length, 0)
  const hasGaps = totalTodos > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{hasGaps ? 'Unresolved evidence gaps' : 'Broken gap markers'}</DialogTitle>
          <DialogDescription>
            {hasGaps
              ? 'This draft still has evidence gaps. Exporting removes the gap markers — the surrounding claims will read as finished prose without their supporting evidence.'
              : 'This draft has a gap marker the export cannot remove. It will appear in the exported document exactly as written.'}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[45vh] overflow-y-auto space-y-4">
          {sections.map((section, index) => (
            <div key={`${section.sectionKey}-${index}`} className="space-y-1.5">
              <p className="text-sm font-medium text-foreground">{section.title}</p>
              <ul className="space-y-1.5">
                {section.todos.map((todo, todoIndex) => (
                  <li key={todoIndex} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <span
                      aria-hidden
                      className="mt-[0.4rem] h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--status-warning))]"
                    />
                    <span>{todo.trim() === '' ? 'Unlabelled gap' : todo}</span>
                  </li>
                ))}
                {section.malformedCount > 0 && (
                  <li className="flex items-start gap-2 text-sm text-[hsl(var(--destructive))]">
                    <AlertTriangle aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                      {section.malformedCount === 1
                        ? 'This section contains a broken gap marker that will appear in the exported document — fix it in the editor.'
                        : `This section contains ${section.malformedCount} broken gap markers that will appear in the exported document — fix them in the editor.`}
                    </span>
                  </li>
                )}
              </ul>
            </div>
          ))}
        </div>

        {attachmentCount > 0 && (
          <p className="text-sm text-muted-foreground">
            {attachmentCount === 1
              ? '1 attachment attribution will also be removed.'
              : `${attachmentCount} attachment attributions will also be removed.`}
          </p>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onReviewGaps}>
            Review gaps
          </Button>
          <Button onClick={onExportAnyway}>Export anyway</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
